import {
  Typography,
  Button,
  Input,
  Tooltip,
  IconButton
} from "@material-tailwind/react";
import { TrashIcon, PencilIcon, ExclamationTriangleIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
import {
  ExtendedWoPart,
  PartEditData,
  isCreatedReference,
} from "../../../hooks/parts/useWorkorderParts";
import PartSearchInput from "./PartSearchInput";
import Switch from "../../switch/Switch";
import PartAttachments from "./PartAttachments";
import { nextStateForDeskToggle } from "./partDeskToggle";

interface PartTableRowProps {
  partItem: ExtendedWoPart;
  index: number;
  workorderId: string;
  machineId?: string;
  hoseId?: string;
  targetType?: "workorder" | "machine" | "hose";
  isEditing: boolean;
  isDraft: boolean;
  existingPartIds?: string[];
  editedPartData?: PartEditData;
  canEdit: boolean;
  setEditedPartData?: (data: PartEditData) => void;
  onEditClick: (part: ExtendedWoPart) => void;
  onDeleteClick: (partId: string) => void;
  onSaveClick: (part: ExtendedWoPart) => void;
  onCancelClick: () => void;
  onSearchQueryChange?: (query: string) => void;
  onSelectPart?: (part: EasyTech.Part) => void;
  computeSellingPrice?: (cost: number) => number;
}

/**
 * Composant pour afficher une ligne de part dans le tableau
 * Modes : normal, édition, brouillon
 */
export default function PartTableRow({
  partItem,
  index,
  workorderId,
  machineId = "",
  hoseId,
  targetType = "workorder",
  isEditing,
  isDraft,
  existingPartIds,
  editedPartData,
  setEditedPartData,
  onEditClick,
  onDeleteClick,
  onSaveClick,
  onCancelClick,
  onSearchQueryChange,
  onSelectPart,
  canEdit,
  computeSellingPrice,
}: PartTableRowProps) {

  const getPrice = (data: any) => {
    return data.desk ? data.price : (data.part && data.part.price ? data.part.price : 0);
  };

  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null) return "";
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const getTotalPrice = (data: any) => {
    if (!data) return "";

    const price = getPrice(data);

    if (!data.quantity)
      return "No quantity"; //TODO trad
    if (price === undefined || price === null)
      return "No part price"; //TODO trad

    const roundedPrice = Math.round(price * 100) / 100;
    return formatNumber(roundedPrice * data.quantity);
  };

  // Mode édition ou brouillon
  if (isEditing || isDraft) {
    const editRow = (
      <tr key="edit" className={index % 2 === 0 ? "bg-white" : "bg-blue-gray-50/50"}>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {editedPartData && (
            editedPartData.desk ? (
              <Input
                value={editedPartData.searchQuery}
                onChange={(e) => {
                  const newVal = e.target.value;
                  onSearchQueryChange && onSearchQueryChange(newVal);
                  // Update part label as well if part exists or create structure
                  if (setEditedPartData) {
                    setEditedPartData({
                      ...editedPartData,
                      searchQuery: newVal,
                      part: {
                        ...(editedPartData.part || {}),
                        label: newVal
                      }
                    });
                  }
                }}
              />
            ) : (
              <PartSearchInput
                value={editedPartData.searchQuery}
                onChange={(value) => {
                  onSearchQueryChange && onSearchQueryChange(value);
                  if (setEditedPartData && editedPartData.part && value !== editedPartData.part.label) {
                    setEditedPartData({
                      ...editedPartData,
                      searchQuery: value,
                      part: null,
                      price: 0,
                      purchasePrice: 0,
                      rate: 0
                    } as any);
                  }
                }}
                onSelectPart={(part) => {
                  onSelectPart && onSelectPart(part);
                }}
                workorderId={workorderId}
                targetType={targetType}
                machineId={machineId}
                hoseId={hoseId}
                existingPartIds={existingPartIds}
                spanTwoColumns={true}
              />
            )
          )}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {editedPartData?.desk ? (
            <div className="relative flex w-full">
              <div className="flex items-center rounded-l-lg border border-r-0 border-blue-gray-200 bg-blue-gray-50/50 px-3 text-sm text-blue-gray-500 whitespace-nowrap">
                EXT_
              </div>
              <Input
                value={editedPartData.part?.ref ? editedPartData.part.ref.replace(/^EXT_/, '') : ""}
                onChange={(e) => {
                  if (setEditedPartData) {
                    setEditedPartData({
                      ...editedPartData,
                      part: {
                        ...(editedPartData.part || {}),
                        ref: `EXT_${e.target.value}`
                      }
                    });
                  }
                }}
                className="rounded-l-none bg-white shadow-none"
                labelProps={{
                  className: "before:content-none after:content-none",
                }}
                containerProps={{
                  className: "min-w-0 h-10",
                }}
                placeholder="Référence"
              />
            </div>
          ) : (
            editedPartData?.part?.ref || "-"
          )}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {editedPartData && setEditedPartData && (
            <div className="min-w-0">
              <Input
                type="number"
                min="1"
                value={editedPartData.quantity}
                onChange={(e) =>
                  setEditedPartData({
                    ...editedPartData,
                    quantity: Number(e.target.value),
                  })
                }
                containerProps={{ className: "!min-w-0" }}
              />
            </div>
          )}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {editedPartData && setEditedPartData && (
            <div className="min-w-0">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editedPartData.desk ? (editedPartData.purchasePrice ?? "") : ""}
                disabled={!editedPartData.desk}
                onChange={(e) => {
                  const newCost = Number(e.target.value);
                  const newPrice = computeSellingPrice && editedPartData.desk ? computeSellingPrice(newCost) : editedPartData.price;
                  setEditedPartData({
                    ...editedPartData,
                    purchasePrice: newCost,
                    price: newPrice ? Number(newPrice.toFixed(2)) : 0
                  });
                }}
                containerProps={{ className: "!min-w-0" }}
              />
            </div>
          )}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {editedPartData && setEditedPartData && (
            <div className="min-w-0">
              <Input
                step="0.01"
                type="number"
                value={editedPartData.desk ? (editedPartData.price ?? "") : (typeof getPrice(editedPartData) === "number" ? Number(getPrice(editedPartData)).toFixed(2) : "0.00")}
                disabled={true}
                onChange={(e) =>
                  setEditedPartData({
                    ...editedPartData,
                    price: Number(e.target.value),
                  })
                }
                containerProps={{ className: "!min-w-0" }}
              />
            </div>
          )}
        </td>
        <td className="overflow-hidden whitespace-nowrap border-b border-gray-200 p-2">
          {editedPartData && setEditedPartData && getTotalPrice(editedPartData)}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {editedPartData && setEditedPartData && (
            <div className="flex justify-center">
              <Switch
                checked={editedPartData.desk}
                disabled={!isDraft && partItem.desk}
                onChange={(isChecked) =>
                  setEditedPartData(
                    nextStateForDeskToggle(
                      editedPartData,
                      isChecked,
                      partItem,
                      computeSellingPrice
                    )
                  )
                }
              />
            </div>
          )}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {editedPartData?.desk && setEditedPartData ? (
            <Input
              value={editedPartData.documentReference ?? ""}
              onChange={(e) =>
                setEditedPartData({
                  ...editedPartData,
                  documentReference: e.target.value,
                })
              }
              containerProps={{ className: "!min-w-0" }}
            />
          ) : (
            <Typography variant="small" color="blue-gray">
              -
            </Typography>
          )}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          {!editedPartData?.desk ? (
            <Typography variant="small" className="text-xs text-blue-gray-400">
              -
            </Typography>
          ) : isDraft ? (
            // Un justificatif se rattache à une ligne : il faut qu'elle existe d'abord.
            <Typography variant="small" className="text-xs text-blue-gray-400">
              {t("workorder:parts:attachments:afterSave")}
            </Typography>
          ) : (
            <PartAttachments
              workorderId={workorderId}
              partLineId={partItem.id}
              machineId={machineId || undefined}
              canEdit={canEdit}
            />
          )}
        </td>
        <td className="overflow-hidden border-b border-gray-200 p-2">
          <div className="flex justify-end gap-2">
            <Tooltip content={t("workorder:parts:buttons:cancel")}>
              <Button
                variant="filled"
                color="white"
                aria-label={t("workorder:parts:buttons:cancel")}
                className="btn-cancel border p-2"
                onClick={onCancelClick}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip content={t("workorder:parts:buttons:save")}>
              <Button
                variant="filled"
                aria-label={t("workorder:parts:buttons:save")}
                className="bg-blue-500 p-2"
                onClick={() => onSaveClick(partItem)}
                disabled={!editedPartData?.part}
              >
                <CheckIcon className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        </td>
      </tr>
    );

    return editRow;
  }

  // Mode normal
  return (
    <tr className="bg-white">
      <td className="p-4 border-b border-gray-200">
        <Typography variant="small" color={partItem.part ? "blue-gray" : "red"} className="font-medium">
          {partItem.part?.label || "Pièce inexistante"}
        </Typography>
      </td>
      <td className="p-4 border-b border-gray-200">
        <Typography variant="small" color="blue-gray">
          {partItem.part?.ref || "-"}
        </Typography>
      </td>
      <td className="overflow-hidden border-b border-gray-200 p-2">
        <Typography variant="small" color="blue-gray" className="truncate">
          {partItem.quantity}
        </Typography>
      </td>
      <td className="p-4 border-b border-gray-200">
        <Typography variant="small" color="blue-gray">
          {partItem.desk ? formatNumber(partItem.purchasePrice || partItem.part?.purchasePrice || 0) : ""}
        </Typography>
      </td>
      <td className="p-4 border-b border-gray-200">
        <Typography variant="small" color="blue-gray">
          {formatNumber(getPrice(partItem) || 0)}
        </Typography>
      </td>
      <td className="p-4 border-b border-gray-200">
        <Typography variant="small" color="blue-gray" className="font-medium">
          {getTotalPrice(partItem)}
        </Typography>
      </td>
      <td className="p-4 border-b border-gray-200 text-center">
        <div className="flex flex-col items-center">
          {partItem.desk ? (
            <>
              <div className="flex items-center gap-1">
                <p>{t("workorder:parts:yes")}</p>
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
              </div>
              {/* Comment la référence a été obtenue qualifie le « comptoir » : ça n'a pas de sens
                  pour une pièce de camion, donc ça vit ici plutôt que dans sa propre colonne. */}
              <Typography variant="small" className="text-xs text-blue-gray-400">
                {isCreatedReference(partItem)
                  ? t("workorder:parts:referenceOrigin:created")
                  : t("workorder:parts:referenceOrigin:reused")}
              </Typography>
            </>
          ) : (
            <div className="flex items-center gap-1">
              <p>{t("workorder:parts:no")}</p>
            </div>
          )}
        </div>
      </td>
      <td className="overflow-hidden border-b border-gray-200 p-2">
        <Typography variant="small" color="blue-gray" className="truncate">
          {partItem.documentReference || "-"}
        </Typography>
      </td>
      <td className="overflow-hidden border-b border-gray-200 p-2">
        {partItem.desk ? (
          <PartAttachments
            workorderId={workorderId}
            partLineId={partItem.id}
            machineId={machineId || undefined}
            canEdit={canEdit}
          />
        ) : (
          <Typography variant="small" color="blue-gray">
            -
          </Typography>
        )}
      </td>
      <td className="overflow-hidden border-b border-gray-200 p-2">
        <div className="flex justify-end">
          <Tooltip content={t("workorder:parts:edit")}>
            <IconButton
              variant="text"
              color="blue"
              onClick={() => onEditClick(partItem)}
              disabled={!canEdit || !partItem.part}
            >
              <PencilIcon className="h-4 w-4 font-800" />
            </IconButton>
          </Tooltip>
          <Tooltip content={t("workorder:parts:delete")}>
            <IconButton
              variant="text"
              color="blue"
              onClick={() => onDeleteClick(partItem.id)}
              disabled={!canEdit}
            >
              <TrashIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}
